import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  Clock,
  Target,
  Users,
  Filter,
  Download,
  ChevronRight
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { RiskBadge } from '../../components/shared/RiskBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

const dropoutRiskPatients = [
  { id: 1, name: 'Ahmad Bin Ismail', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', service: 'Physiotherapy', riskLevel: 'high', riskScore: 92, predictedDate: '7 days', reason: 'Missed 3 consecutive sessions', intervention: 'Schedule call' },
  { id: 2, name: 'Mei Ling Wong', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100', service: 'OT', riskLevel: 'high', riskScore: 87, predictedDate: '10 days', reason: 'Declining engagement score', intervention: 'Therapist outreach' },
  { id: 3, name: 'Ravi Kumar', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100', service: 'Speech Therapy', riskLevel: 'high', riskScore: 84, predictedDate: '12 days', reason: 'Payment issues flagged', intervention: 'Offer payment plan' },
  { id: 4, name: 'Siti Aminah', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100', service: 'Physiotherapy', riskLevel: 'medium', riskScore: 72, predictedDate: '18 days', reason: 'Transportation challenges', intervention: 'Home visit option' },
  { id: 5, name: 'David Tan', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100', service: 'Diet & Nutrition', riskLevel: 'medium', riskScore: 68, predictedDate: '21 days', reason: 'Low session satisfaction', intervention: 'Assign new dietitian' },
];

const recoveryDelayPatients = [
  { id: 1, name: 'Sarah Lee', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100', service: 'Physiotherapy', condition: 'ACL Recovery', expectedWeeks: 12, predictedWeeks: 16, delay: '+4 weeks', reason: 'Inconsistent exercise compliance' },
  { id: 2, name: 'John Ng', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100', service: 'OT', condition: 'Stroke Rehab', expectedWeeks: 24, predictedWeeks: 30, delay: '+6 weeks', reason: 'Plateau in motor function progress' },
  { id: 3, name: 'Priya Devi', avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100', service: 'Speech Therapy', condition: 'Aphasia', expectedWeeks: 16, predictedWeeks: 20, delay: '+4 weeks', reason: 'Missing follow-up exercises' },
];

const plateauPatients = [
  { id: 1, name: 'Michael Ooi', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100', service: 'Physiotherapy', condition: 'Lower Back Pain', weeksAtPlateau: 3, currentScore: 65, peakScore: 68, suggestion: 'Consider manual therapy progression' },
  { id: 2, name: 'Fatimah Hassan', avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100', service: 'OT', condition: 'Carpal Tunnel', weeksAtPlateau: 2, currentScore: 72, peakScore: 74, suggestion: 'Add strengthening exercises' },
];

export default function PredictiveInsights() {
  const [selectedTab, setSelectedTab] = useState('dropout');
  const [serviceFilter, setServiceFilter] = useState('all');

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Predictive Insights</h1>
          <p className="text-sm text-muted-foreground mt-1">AI-powered predictions for patient outcomes and risks</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Services" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              <SelectItem value="physio">Physiotherapy</SelectItem>
              <SelectItem value="ot">Occupational Therapy</SelectItem>
              <SelectItem value="speech">Speech Therapy</SelectItem>
              <SelectItem value="diet">Diet & Nutrition</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-primary">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary-light flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">High Dropout Risk</p>
                <p className="text-2xl font-display font-bold text-foreground">23</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-warning">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning-light flex items-center justify-center">
                <Clock className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recovery Delays</p>
                <p className="text-2xl font-display font-bold text-foreground">15</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-info">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-info-light flex items-center justify-center">
                <Target className="w-5 h-5 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Plateau Detected</p>
                <p className="text-2xl font-display font-bold text-foreground">8</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-success">
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success-light flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">On Track</p>
                <p className="text-2xl font-display font-bold text-foreground">2,801</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Prediction Tables */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList>
          <TabsTrigger value="dropout" className="gap-2">
            <AlertTriangle className="w-4 h-4" />
            Dropout Risk
          </TabsTrigger>
          <TabsTrigger value="delay" className="gap-2">
            <Clock className="w-4 h-4" />
            Recovery Delay
          </TabsTrigger>
          <TabsTrigger value="plateau" className="gap-2">
            <Target className="w-4 h-4" />
            Plateau Detection
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="dropout" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Dropout Risk Prediction</CardTitle>
              <CardDescription>Top 20 patients most likely to discontinue treatment</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Risk Level</TableHead>
                    <TableHead>Predicted In</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dropoutRiskPatients.map((patient) => (
                    <TableRow key={patient.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={patient.avatar} />
                            <AvatarFallback>{patient.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <span className="font-medium">{patient.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{patient.service}</Badge>
                      </TableCell>
                      <TableCell>
                        <RiskBadge level={patient.riskLevel} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{patient.predictedDate}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{patient.reason}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">{patient.intervention}</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="delay" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recovery Delay Predictions</CardTitle>
              <CardDescription>Patients predicted to take longer than expected to recover</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Expected</TableHead>
                    <TableHead>Predicted</TableHead>
                    <TableHead>Delay</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recoveryDelayPatients.map((patient) => (
                    <TableRow key={patient.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={patient.avatar} />
                            <AvatarFallback>{patient.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{patient.name}</p>
                            <p className="text-xs text-muted-foreground">{patient.service}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{patient.condition}</TableCell>
                      <TableCell>{patient.expectedWeeks} weeks</TableCell>
                      <TableCell>{patient.predictedWeeks} weeks</TableCell>
                      <TableCell>
                        <Badge className="bg-warning-light text-warning">{patient.delay}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{patient.reason}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="plateau" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Plateau Detection</CardTitle>
              <CardDescription>Patients showing stagnation in recovery progress</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Patient</TableHead>
                    <TableHead>Condition</TableHead>
                    <TableHead>Weeks at Plateau</TableHead>
                    <TableHead>Current Score</TableHead>
                    <TableHead>Peak Score</TableHead>
                    <TableHead>AI Suggestion</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plateauPatients.map((patient) => (
                    <TableRow key={patient.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-8 h-8">
                            <AvatarImage src={patient.avatar} />
                            <AvatarFallback>{patient.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{patient.name}</p>
                            <p className="text-xs text-muted-foreground">{patient.service}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{patient.condition}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{patient.weeksAtPlateau} weeks</Badge>
                      </TableCell>
                      <TableCell>{patient.currentScore}%</TableCell>
                      <TableCell>{patient.peakScore}%</TableCell>
                      <TableCell className="text-sm text-info max-w-[200px]">{patient.suggestion}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
